import { getHashParams } from "common";
import Cookies from "js-cookie";
import create from "zustand";
import { clearLastLoginMethod } from "@/auth/authMethod";
import { clearExpiredIdleSession } from "@/auth/idleSession";
import type { SignOutReason } from "@/auth/signOut";

const cookies = Cookies.withAttributes({
    expires: 14, // Two weeks.
    secure: true,
    domain: window.location.hostname,
    sameSite: "Strict", // Strict is fine since we only use it for remember me.
});

const hashParams = getHashParams(true); // Format /dashboard#seller=<id></id>&apiKey=<key>

// Impersonation and account switch sign in via the hash; a timed-out idle session must not log them straight out.
if (hashParams.has("apiKey")) {
    clearExpiredIdleSession();
}

export interface AccountStoreState {
    apiKey: string | null;
    seller: ID | null;
    rememberMe: boolean;

    hasAPIKeyinHash: boolean;

    setSeller: (seller: ID, shouldRemember: boolean) => void;
    setApiKey: (apiKey: string, shouldRemember: boolean) => void;

    clearApiKey: () => void;
    hasSavedLogin: () => boolean;
    isChatLoaded: boolean;
    setIsChatLoaded: (isChatLoaded: boolean) => void;

    /**
     * Resets authentication state and cookies, and redirects user to login screen if required
     *
     * @param reload Do we need to redirect user to login screen after resetting authentication state
     */
    forget: (reload?: boolean, reason?: SignOutReason) => void;

    isSessionExpiredModalVisible: boolean;

    toggleSessionExpiredModal: (status?: boolean) => void;
}

export const useAccountStore = create<AccountStoreState>((set, get) => ({
    apiKey: hashParams.get("apiKey") ?? cookies.get("apiKey") ?? null,
    seller: hashParams.get("seller") ?? cookies.get("seller") ?? null,
    // Router returns rememberMe in hashParam and will be "1" or "0". Incase we refresh the page after we set the
    // apiKey, we need to check if there is a saved login, so that if saved login is there, then rememberMe is true, else false.
    rememberMe: hashParams.has("rememberMe") ? hashParams.get("rememberMe") === "1" : !!cookies.get("apiKey"),
    hasAPIKeyinHash: hashParams.has("apiKey"),

    isSessionExpiredModalVisible: false,
    isChatLoaded: false,

    setIsChatLoaded: (isChatLoaded) => {
        set((state) => ({ ...state, isChatLoaded }));
    },

    setApiKey(apiKey: string, shouldRemember: boolean) {
        set({ apiKey });

        if (shouldRemember) {
            cookies.set("apiKey", apiKey);
        }
    },

    clearApiKey() {
        cookies.remove("apiKey");
        set({ apiKey: null });
    },

    setSeller(seller: ID, shouldRemember: boolean) {
        set({ seller });

        if (shouldRemember) {
            cookies.set("seller", seller);
        }
    },

    hasSavedLogin() {
        return !!cookies.get("seller");
    },

    forget(reload = true, reason?: SignOutReason) {
        cookies.remove("apiKey");
        cookies.remove("seller");
        clearLastLoginMethod();

        if (reload) {
            window.location.replace(reason ? `/?reason=${reason}` : "/");
        }
    },

    toggleSessionExpiredModal: (status) => {
        set(() => ({ isSessionExpiredModalVisible: status ?? !get().isSessionExpiredModalVisible }));
    },
}));
